import { useState, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_LENGTH } from './constants/lms-course-review.constants';
import { searchResources } from './services/resource-search.service';

/**
 * Debounced course-search hook. Only runs against the search-resources endpoint
 * in response to an explicit search term entered by the admin — suggestions are
 * never fetched here automatically.
 */
const useResourceSearchHook = (searchInput) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (term) => {
    const trimmed = (term || '').trim();
    if (trimmed.length < SEARCH_MIN_LENGTH) {
      setOptions([]);
      return [];
    }

    setLoading(true);
    try {
      const results = await searchResources(trimmed);
      setOptions(results);
      return results;
    } catch {
      setOptions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((term) => runSearch(term), SEARCH_DEBOUNCE_MS),
    [runSearch]
  );

  useEffect(() => {
    debouncedSearch(searchInput);
    return () => debouncedSearch.cancel();
  }, [searchInput, debouncedSearch]);

  return { options, loading };
};

export default useResourceSearchHook;
